import { useEffect, useRef, type MutableRefObject } from 'react'
import { useEscape } from './useEscape'

/**
 * A dialog that holds the keyboard while it is open.
 *
 * `aria-modal="true"` is a promise to a screen reader: nothing outside this
 * matters while it is open. Nothing kept it. Focus stayed on the button that
 * opened the dialog, so Tab walked the page BEHIND the scrim — invisible under
 * the overlay, and announced as if the dialog were not there. A writer using a
 * keyboard reached the commit box by tabbing through the whole application, or
 * did not reach it.
 *
 * Three things, which are one thing: focus moves in when it opens, Tab cycles
 * inside it, and focus goes back where it came from when it closes. The last is
 * what makes a dialog feel like a step rather than a place — press Escape and
 * the keyboard is back on the button that was pressed.
 *
 * One hook, for the reason `useEscape` is one hook: several copies of a
 * `keydown` listener on `window` is several chances to leave one behind, and a
 * stray handler moves somebody's focus in a dialog nobody has open.
 */

/** What a keyboard can land on. Order matters: it is the Tab order. */
const REACHABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(', ')

const reachableIn = (panel: HTMLElement): HTMLElement[] =>
  [...panel.querySelectorAll<HTMLElement>(REACHABLE)].filter(
    // Drawn, and not hidden by an ancestor: a control in a collapsed section is
    // not in the Tab order, and treating it as the last one sends Tab nowhere.
    (element) => element.offsetParent !== null || element.getClientRects().length > 0,
  )

export function useDialog<T extends HTMLElement>(
  open: boolean,
  close: () => void,
): MutableRefObject<T | null> {
  const panel = useRef<T | null>(null)
  useEscape(open, close)

  useEffect(() => {
    if (!open) return
    /*
     * Where the keyboard was, so it can be put back. Read on open rather than
     * on close: by then the element may be gone, and `document.activeElement`
     * is already the body.
     */
    const before = document.activeElement as HTMLElement | null
    /*
     * A frame later, because the panel is mounted by the same render that sets
     * `open` and the effect runs before the browser has laid it out — a control
     * with no box cannot take focus.
     */
    const move = requestAnimationFrame(() => {
      const element = panel.current
      if (!element) return
      const wanted =
        element.querySelector<HTMLElement>('[data-autofocus]') ?? reachableIn(element)[0] ?? element
      // A panel with nothing to focus takes it itself, so the keyboard is at
      // least inside the dialog rather than behind it.
      if (wanted === element && !element.hasAttribute('tabindex')) element.tabIndex = -1
      wanted.focus()
    })
    return () => {
      cancelAnimationFrame(move)
      // Back where it came from — unless it has gone, in which case leaving it
      // to the browser beats focusing something arbitrary.
      if (before && before.isConnected) before.focus()
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return
      const element = panel.current
      if (!element) return
      const items = reachableIn(element)
      if (items.length === 0) return
      const first = items[0]!
      const last = items[items.length - 1]!
      const active = document.activeElement
      // Outside already — a click behind the scrim, or focus never moved in.
      if (!(active instanceof Node) || !element.contains(active)) {
        event.preventDefault()
        ;(event.shiftKey ? last : first).focus()
        return
      }
      if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      } else if (event.shiftKey && active === first) {
        event.preventDefault()
        last.focus()
      }
    }
    window.addEventListener('keydown', onKey, { capture: true })
    return () => window.removeEventListener('keydown', onKey, { capture: true })
  }, [open])

  return panel
}
